import React, { CSSProperties, useContext, useMemo } from 'react';
import {
  DepartmentLabel,
  NameBoxNoWrap,
  ProgramLabel,
  ExportedCardHeaderContainer,
} from 'spw-components/growth-calculator/styles';
import { CalculatorType, Scenario } from 'spw-redux/modules/calculators/types';
import { Box, Menu, MenuItem } from '@mui/material';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import {
  isDepartmentScenario,
  isFloorScenario,
  isMacro,
} from 'spw-components/worksheet/utils';
import { IBuilding, IFloor } from 'spw-redux/modules/projects/types';
import { useESASelectedFloorAndBuildingLabels } from 'spw-esa-projects/program-summary/floor-building-labels-hook';
import {
  AMENITY_SPACES_CATEGORY,
  GROWTH_CALCULATORS,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import { useSelector } from 'react-redux';
import { currentProject } from 'spw-redux/modules/projects/selectors';

import Button from '@mui/material/Button';
import { VisibleCards } from 'spw-esa-projects/program-summary/hooks';
import { ProgramSummaryCardsContext } from 'spw-esa-projects/program-summary/summary-cards';
import { camelCase } from 'lodash';
import { setAllValuesToFalse } from 'spw-projects/reports/utils';
import styled from 'styled-components';
import { PlainProgramScenario } from '../../models/Scenario';

interface ScenarioHeaderProps {
  scenario: Scenario | PlainProgramScenario;
  building?: IBuilding;
  showProjectName?: boolean;
  styles?: CSSProperties;
}

const ProjectNameLabel = styled(Box)`
  display: flex;
  white-space: nowrap;
  margin-right: 32px;
`;

export const ScenarioHeader: React.FunctionComponent<ScenarioHeaderProps> = ({
  scenario,
  building,
  showProjectName = false,
  styles,
}: ScenarioHeaderProps) => {
  const isTotalProgramScenario = isTotalProgram(scenario);
  const isDepartment = isDepartmentScenario(scenario);
  const isFloor = isFloorScenario(scenario?.calculatorType);
  const calculatorType = scenario?.calculatorType?.replace(/_/g, '-');
  const project = useSelector(currentProject);

  let programType;

  if (isMacro(scenario)) {
    programType = 'Macro';
  } else if (
    [
      GROWTH_CALCULATORS.buildingHeadcountSeatcount,
      GROWTH_CALCULATORS.buildingUsf,
    ].includes(calculatorType as CalculatorType)
  ) {
    programType = 'Building';
  } else if (isFloor) {
    programType = building?.floors?.find(
      (floor: IFloor) => floor.id === scenario.floorId
    )?.name;
  }

  return (
    <ExportedCardHeaderContainer style={styles || {}}>
      {showProjectName && (
        <ProjectNameLabel>
          <Box>
            <b>PROJECT NAME:</b>
          </Box>
          <NameBoxNoWrap>{project?.projectName}</NameBoxNoWrap>
        </ProjectNameLabel>
      )}
      <Box display="flex" style={{ whiteSpace: 'nowrap' }}>
        <Box>
          <b>SCENARIO NAME:</b>
        </Box>
        <NameBoxNoWrap>{scenario?.name}</NameBoxNoWrap>
      </Box>

      <Box display="flex">
        {isTotalProgramScenario || (isDepartment && scenario.department) ? (
          <>
            <DepartmentLabel>DEPARTMENT:</DepartmentLabel>
            <NameBoxNoWrap>
              {isTotalProgramScenario
                ? 'Multiple'
                : `${scenario.department.name} ${
                    scenario.department.secondaryIdentifier || ''
                  }`.trim()}
            </NameBoxNoWrap>
          </>
        ) : (
          <>
            <ProgramLabel>{isFloor ? 'FLOOR NAME:' : 'PROGRAM:'}</ProgramLabel>
            <NameBoxNoWrap>{programType}</NameBoxNoWrap>
          </>
        )}
      </Box>
    </ExportedCardHeaderContainer>
  );
};

interface ESAProgramSummaryCardHeaderProps {
  styles?: CSSProperties;
  setVisibleCards: React.Dispatch<React.SetStateAction<VisibleCards>>;
}

const MENU_ITEMS = [
  { id: 'projectOverview', label: 'Project Overview' },
  { id: 'keyDataPoints', label: 'Key Data Points' },
  {
    id: 'spaceTypeAllocationIndividualWorkspacesUsf',
    label: 'Space Type Allocation - Individual Workspaces USF',
  },
  {
    id: 'spaceTypeAllocationSubtotalUsf',
    label: 'Space Type Allocation - Subtotal USF',
  },
  { id: 'headcountUsf', label: 'Headcount USF' },
  { id: 'seatCountUsf', label: 'Seat Count USF' },
  { id: 'deskSharingRatio', label: 'Desk Sharing' },
  { id: 'usfBreakdown', label: 'Square Footage Breakdown' },
  { id: 'meetingSeatRatio', label: 'Meeting Seat Ratio' },
  { id: 'workpoints', label: 'Workpoints' },
  { id: 'seatCountUsfWithWorkpoints', label: 'Seat Count USF with Workpoints' },
  { id: 'deskSharingWithWorkpoints', label: 'Desk Sharing with Workpoints' },
  {
    id: 'alternativeWorkpointsBreakdown',
    label: 'Alternative Workpoints Breakdown',
  },
  { id: 'programSummaryReport', label: 'Program Summary Report' },
];

export const ESAProgramSummaryCardHeader = ({
  styles,
  setVisibleCards,
}: ESAProgramSummaryCardHeaderProps) => {
  const { visibleCards } = useContext(ProgramSummaryCardsContext);
  const project = useSelector(currentProject);
  const { addressLabel, floorLabel } = useESASelectedFloorAndBuildingLabels();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleCardsButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCardMenuItemClick = (card: keyof VisibleCards) => {
    setAnchorEl(null);
    setVisibleCards((prevVisibleCards: VisibleCards) => {
      const newVisibleCards = setAllValuesToFalse(
        prevVisibleCards
      ) as VisibleCards;
      return { ...newVisibleCards, [card]: true };
    });
  };
  const handleCategoryCardMenuItemClick = (category: string, key: string) => {
    setAnchorEl(null);
    setVisibleCards((prevVisibleCards: VisibleCards) => {
      const newVisibleCards = setAllValuesToFalse(
        prevVisibleCards
      ) as VisibleCards;
      const categoryUsfAndSeatMix =
        newVisibleCards.categoryUsfAndSeatMix[category];

      return {
        ...newVisibleCards,
        categoryUsfAndSeatMix: {
          ...newVisibleCards.categoryUsfAndSeatMix,
          [category]: {
            ...categoryUsfAndSeatMix,
            [key]: true,
          },
        },
      };
    });
  };

  const showHeader = useMemo(() => {
    return (
      visibleCards.programSummaryReport ||
      visibleCards.categoryUsfAndSeatMix[INDIVIDUAL_WORKSPACES_CATEGORY]
        .usfAndSeatMix ||
      [
        MEETING_SPACES_CATEGORY,
        AMENITY_SPACES_CATEGORY,
        SUPPORT_SPACES_CATEGORY,
      ].some((category) => {
        return visibleCards.categoryUsfAndSeatMix[category].usfMixAndRatios;
      })
    );
  }, [visibleCards]);

  return (
    <>
      {showHeader && (
        <ExportedCardHeaderContainer style={styles || {}}>
          <Box display="flex" style={{ whiteSpace: 'nowrap' }}>
            <Box>
              <b>PROJECT NAME:</b>
            </Box>
            <NameBoxNoWrap>{project.projectName}</NameBoxNoWrap>
          </Box>
          <Box display="flex">
            <ProgramLabel>STREET ADDRESS:</ProgramLabel>
            <NameBoxNoWrap>{addressLabel}</NameBoxNoWrap>
          </Box>
          <Box display="flex">
            <ProgramLabel>FLOOR NAME:</ProgramLabel>
            <NameBoxNoWrap>{floorLabel}</NameBoxNoWrap>
          </Box>
        </ExportedCardHeaderContainer>
      )}
      <Box
        display="flex"
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
        }}
      >
        <Button onClick={handleCardsButtonClick} id="show-cards" />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={(e: React.BaseSyntheticEvent) => {
            e.stopPropagation();
            setAnchorEl(null);
          }}
        >
          {MENU_ITEMS.map((menuItem) => {
            return (
              <MenuItem
                id={menuItem.id}
                key={menuItem.id}
                onClick={() => {
                  handleCardMenuItemClick(menuItem.id as keyof VisibleCards);
                }}
              >
                {menuItem.label}
              </MenuItem>
            );
          })}
          {['usfAndSeatMix', 'usfMix', 'seatMix'].map((cardType) => {
            const category = INDIVIDUAL_WORKSPACES_CATEGORY;
            return (
              <MenuItem
                key={camelCase(`${category}_${cardType}`)}
                id={camelCase(`${category}_${cardType}`)}
                onClick={() => {
                  handleCategoryCardMenuItemClick(category, cardType);
                }}
              >
                {category} {cardType}
              </MenuItem>
            );
          })}
          {[
            MEETING_SPACES_CATEGORY,
            AMENITY_SPACES_CATEGORY,
            SUPPORT_SPACES_CATEGORY,
          ].map((category) => {
            return ['usfMixAndRatios', 'usfMix', 'ratios'].map((cardType) => {
              return (
                <MenuItem
                  key={camelCase(`${category}_${cardType}`)}
                  id={camelCase(`${category}_${cardType}`)}
                  onClick={() => {
                    handleCategoryCardMenuItemClick(category, cardType);
                  }}
                >
                  {category} {cardType}
                </MenuItem>
              );
            });
          })}
        </Menu>
      </Box>
    </>
  );
};
